import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    Tabs,
    Table,
    Tag,
    Card,
    Space,
    Statistic,
    Row,
    Col,
    Badge,
    Button,
    Modal,
    Form,
    Input,
    InputNumber,
    Select,
    DatePicker,
    message,
    Tooltip,
    Dropdown,
    Grid,
    List,
} from 'antd';
import {
    InboxOutlined,
    PlusOutlined,
    InfoCircleOutlined,
    EnvironmentOutlined,
    DeleteOutlined,
    ExclamationCircleOutlined,
    SwapOutlined,
    DownloadOutlined,
    SyncOutlined,
    CheckOutlined,
    ReloadOutlined,
    HistoryOutlined,
} from '@ant-design/icons';
import { inventoryApi, productsApi, integrationsApi } from '../../api/client';
import { BatchPackagingSection } from '../products/components/BatchPackagingDrawer';
import dayjs from 'dayjs';

export default function InventoryPage() {
    const { useBreakpoint } = Grid;
    const screens = useBreakpoint();
    const isMobile = !screens.md;
    const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
    const [isBatchModalOpen, setIsBatchModalOpen] = useState(false);
    const [isEventModalOpen, setIsEventModalOpen] = useState(false);
    const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
    const [transferringBatch, setTransferringBatch] = useState<any>(null);
    const [editingBatch, setEditingBatch] = useState<any>(null);
    const [editingLocation, setEditingLocation] = useState<any>(null);
    const [filterProduct, setFilterProduct] = useState<string | undefined>();
    const [filterLocation, setFilterLocation] = useState<string | undefined>();
    const [filterBatchStatus, setFilterBatchStatus] = useState<string | undefined>();
    const [locationForm] = Form.useForm();
    const [batchForm] = Form.useForm();
    const [eventForm] = Form.useForm();
    const [transferForm] = Form.useForm();
    const queryClient = useQueryClient();

    const { data: stockData, isLoading: stockLoading } = useQuery({
        queryKey: ['stock', filterProduct, filterLocation],
        queryFn: () => inventoryApi.getStock({ productId: filterProduct, locationId: filterLocation }).then((res) => res.data),
    });

    const { data: batchesData, isLoading: batchesLoading } = useQuery({
        queryKey: ['batches', filterProduct, filterBatchStatus],
        queryFn: () => inventoryApi.getBatches({ productId: filterProduct, status: filterBatchStatus }).then((res) => res.data),
    });

    const { data: locationsData } = useQuery({
        queryKey: ['locations'],
        queryFn: () => inventoryApi.getLocations().then((res) => res.data),
    });

    const { data: eventsData, isLoading: eventsLoading } = useQuery({
        queryKey: ['inventory-events'],
        queryFn: () => inventoryApi.getEvents({ limit: 50 }).then((res) => res.data),
    });

    const { data: productsData } = useQuery({
        queryKey: ['products-for-inventory'],
        queryFn: () => productsApi.getAll({}).then((res) => res.data),
    });

    const { data: lowStockAlertsData } = useQuery({
        queryKey: ['low-stock-alerts'],
        queryFn: () => inventoryApi.getLowStockAlerts().then((res: any) => res.data),
    });

    // Full alerts from InventoryAlert entity
    const { data: alertsData, isLoading: alertsLoading } = useQuery({
        queryKey: ['inventory-alerts'],
        queryFn: () => inventoryApi.getAlerts().then((res: any) => res.data),
    });

    const createLocationMutation = useMutation({
        mutationFn: (data: any) => inventoryApi.createLocation(data).then((res) => res.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['locations'] });
            message.success('Lokalizacja utworzona');
            setIsLocationModalOpen(false);
            locationForm.resetFields();
        },
        onError: () => message.error('Błąd tworzenia lokalizacji'),
    });

    const updateLocationMutation = useMutation({
        mutationFn: ({ id, data }: { id: string; data: any }) => inventoryApi.updateLocation(id, data).then((res) => res.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['locations'] });
            message.success('Lokalizacja zaktualizowana');
            setIsLocationModalOpen(false);
            setEditingLocation(null);
            locationForm.resetFields();
        },
        onError: () => message.error('Błąd aktualizacji lokalizacji'),
    });

    const createBatchMutation = useMutation({
        mutationFn: (data: any) => inventoryApi.createBatch(data).then((res) => res.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['batches'] });
            queryClient.invalidateQueries({ queryKey: ['stock'] });
            message.success('Partia utworzona');
            setIsBatchModalOpen(false);
            batchForm.resetFields();
        },
        onError: () => message.error('Błąd tworzenia partii'),
    });

    const createEventMutation = useMutation({
        mutationFn: (data: any) => inventoryApi.createEvent(data).then((res) => res.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['inventory-events'] });
            queryClient.invalidateQueries({ queryKey: ['stock'] });
            queryClient.invalidateQueries({ queryKey: ['batches'] });
            message.success('Zdarzenie utworzone');
            setIsEventModalOpen(false);
            eventForm.resetFields();
        },
        onError: () => message.error('Błąd tworzenia zdarzenia'),
    });

    const deleteBatchMutation = useMutation({
        mutationFn: (id: string) => inventoryApi.deleteBatch(id).then((res) => res.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['batches'] });
            queryClient.invalidateQueries({ queryKey: ['stock'] });
            message.success('Partia usunięta');
        },
        onError: () => message.error('Błąd usuwania partii'),
    });

    const deleteLocationMutation = useMutation({
        mutationFn: (id: string) => inventoryApi.deleteLocation(id).then((res) => res.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['locations'] });
            message.success('Lokalizacja usunięta');
        },
        onError: () => message.error('Błąd usuwania lokalizacji'),
    });

    const confirmDeleteBatch = (id: string, lotNumber: string) => {
        Modal.confirm({
            title: 'Czy na pewno chcesz usunąć tę partię?',
            icon: <ExclamationCircleOutlined />,
            content: `Partia: ${lotNumber}`,
            okText: 'Usuń',
            okType: 'danger',
            cancelText: 'Anuluj',
            onOk: () => deleteBatchMutation.mutate(id),
        });
    };

    const transferBatchMutation = useMutation({
        mutationFn: (data: { batchId: string; fromLocationId: string; toLocationId: string; quantity: number; notes?: string }) =>
            inventoryApi.transferBatch(data).then((res: any) => res.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['batches'] });
            queryClient.invalidateQueries({ queryKey: ['stock'] });
            queryClient.invalidateQueries({ queryKey: ['events'] });
            message.success('Transfer zakończony pomyślnie');
            setIsTransferModalOpen(false);
            setTransferringBatch(null);
            transferForm.resetFields();
        },
        onError: (error: any) => {
            message.error(error.response?.data?.message || 'Błąd transferu');
        },
    });

    const openTransferModal = (batch: any) => {
        setTransferringBatch(batch);
        transferForm.setFieldsValue({
            fromLocationId: batch.locationId,
            quantity: 1,
        });
        setIsTransferModalOpen(true);
    };

    const confirmDeleteLocation = (id: string, name: string) => {
        Modal.confirm({
            title: 'Czy na pewno chcesz usunąć tę lokalizację?',
            icon: <ExclamationCircleOutlined />,
            content: `Lokalizacja: ${name}`,
            okText: 'Usuń',
            okType: 'danger',
            cancelText: 'Anuluj',
            onOk: () => deleteLocationMutation.mutate(id),
        });
    };

    const updateBatchMutation = useMutation({
        mutationFn: ({ id, data }: { id: string; data: any }) =>
            inventoryApi.updateBatch(id, data).then((res: any) => res.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['batches'] });
            queryClient.invalidateQueries({ queryKey: ['stock'] });
            message.success('Partia zaktualizowana');
            setIsBatchModalOpen(false);
            setEditingBatch(null);
            batchForm.resetFields();
        },
        onError: () => message.error('Błąd aktualizacji partii'),
    });

    // Alert mutations
    const acknowledgeAlertMutation = useMutation({
        mutationFn: ({ id, notes }: { id: string; notes?: string }) =>
            inventoryApi.acknowledgeAlert(id, notes).then((res: any) => res.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['inventory-alerts'] });
            message.success('Alert potwierdzony');
        },
        onError: () => message.error('Błąd potwierdzania alertu'),
    });

    const resolveAlertMutation = useMutation({
        mutationFn: (id: string) => inventoryApi.resolveAlert(id).then((res: any) => res.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['inventory-alerts'] });
            message.success('Alert rozwiązany');
        },
        onError: () => message.error('Błąd rozwiązywania alertu'),
    });

    const dismissAlertMutation = useMutation({
        mutationFn: (id: string) => inventoryApi.dismissAlert(id).then((res: any) => res.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['inventory-alerts'] });
            message.success('Alert odrzucony');
        },
        onError: () => message.error('Błąd odrzucania alertu'),
    });

    const generateAlertsMutation = useMutation({
        mutationFn: () => inventoryApi.generateAlerts().then((res: any) => res.data),
        onSuccess: (data: any) => {
            queryClient.invalidateQueries({ queryKey: ['inventory-alerts'] });
            message.success(`Wygenerowano ${data.created} alertów, rozwiązano ${data.resolved}`);
        },
        onError: () => message.error('Błąd generowania alertów'),
    });

    const handleEditBatch = (record: any) => {
        setEditingBatch(record);
        batchForm.setFieldsValue({
            ...record,
            productId: record.productId,
            locationId: record.locationId,
            expiryDate: record.expiryDate ? dayjs(record.expiryDate) : null,
            productionDate: record.productionDate ? dayjs(record.productionDate) : null,
        });
        setIsBatchModalOpen(true);
    };

    // Aggregate stock data by product for expandable table
    const aggregatedStock = (() => {
        if (!stockData) return [];
        const map = new Map<string, { productId: string; productName: string; totalAvailable: number; total: number; children: any[] }>();
        (stockData as any[]).forEach((s: any) => {
            const key = s.productId;
            if (!map.has(key)) {
                map.set(key, {
                    productId: s.productId,
                    productName: s.productName,
                    totalAvailable: 0,
                    total: 0,
                    children: [],
                });
            }
            const entry = map.get(key)!;

            // 1. FILTER: Ignore rows with 0 quantity that don't represent a real location
            const hasRealLocation = s.locationId || (s.locationName && !['Brak stanu', 'Wszystkie lokalizacje', 'Brak lokalizacji'].includes(s.locationName));
            const hasStock = (s.available && s.available > 0) || (s.total && s.total > 0);

            if (hasRealLocation || hasStock) {
                // 2. UNIFY: Map missing/ghost locations to 'Magazyn główny'
                const resolvedLocationId = s.locationId || 'default';
                const resolvedLocationName = hasRealLocation ? s.locationName : 'Magazyn główny';

                // 3. DEDUPLICATE: Merge entries for the same location
                const existingLoc = entry.children.find(c => c.locationId === resolvedLocationId || c.productName === resolvedLocationName);
                if (existingLoc) {
                    existingLoc.totalAvailable += s.available || 0;
                    existingLoc.total += s.total || 0;
                } else {
                    entry.children.push({
                        // Unified keys and property names to match parent columns for perfect alignment
                        key: `${s.productId}-${resolvedLocationId}`,
                        productId: s.productId,
                        locationId: resolvedLocationId,
                        productName: resolvedLocationName, // Put location name in 'Product' column for children
                        totalAvailable: s.available || 0,   // Map 'available' to 'totalAvailable'
                        total: s.total || 0,
                        isChild: true,
                    });
                }
            }

            entry.totalAvailable += s.available || 0;
            entry.total += s.total || 0;
        });

        // Ensure totalAvailable and total are not double counted 
        map.forEach(entry => {
            if (entry.children.length > 0) {
                entry.totalAvailable = entry.children.reduce((sum, c) => sum + (c.totalAvailable || 0), 0);
                entry.total = entry.children.reduce((sum, c) => sum + (c.total || 0), 0);
            }
        });

        return Array.from(map.values()).map(entry => ({
            ...entry,
            key: entry.productId,
            isChild: false,
        }));
    })();

    const stockColumns = [
        {
            title: 'Produkt / Lokalizacja',
            dataIndex: 'productName',
            key: 'productName',
            render: (text: string, record: any) => (
                <span style={{ fontWeight: record.isChild ? 400 : 600 }}>
                    {text}
                </span>
            )
        },
        {
            title: 'Dostępne',
            dataIndex: 'totalAvailable',
            key: 'totalAvailable',
            render: (val: number) => <Tag color={(val || 0) > 0 ? 'green' : 'red'}>{val || 0} szt.</Tag>,
        },
        { title: 'Ilość', dataIndex: 'total', key: 'total', align: 'right' as const },
    ];

    const stockChildColumns = [
        { title: 'Lokalizacja', dataIndex: 'locationName', key: 'locationName' },
        {
            title: 'Dostępne',
            dataIndex: 'available',
            key: 'available',
            render: (val: number) => <Tag color={(val || 0) > 0 ? 'green' : 'red'}>{val || 0} szt.</Tag>,
        },
        { title: 'Ilość', dataIndex: 'total', key: 'total', align: 'right' as const },
    ];

    const batchColumns = [
        { title: 'LOT', dataIndex: 'lotNumber', key: 'lotNumber' },
        { title: 'Produkt', dataIndex: ['product', 'name'], key: 'product' },
        {
            title: 'Lokalizacja',
            key: 'location',
            render: (_: any, record: any) => {
                // Use stockLocations array from findAllWithStock
                const locations = record.stockLocations || [];
                if (locations.length === 0) {
                    return <Tag>Brak stanu</Tag>;
                }
                if (locations.length === 1) {
                    return <span>{locations[0].locationName}: <strong>{locations[0].quantity} szt.</strong></span>;
                }
                // Multiple locations - show compact list
                return (
                    <Space direction="vertical" size={0}>
                        {locations.map((loc: any, i: number) => (
                            <span key={i} style={{ fontSize: '12px' }}>
                                {loc.locationName}: <strong>{loc.quantity}</strong>
                            </span>
                        ))}
                    </Space>
                );
            },
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 140,
            render: (status: string, record: any) => {
                const statusOptions = [
                    { value: 'ORDERED', label: 'Zamówiona', color: 'blue' },
                    { value: 'IN_TRANSIT', label: 'W drodze', color: 'orange' },
                    { value: 'RECEIVED', label: 'Przyjęta', color: 'cyan' },
                    { value: 'AVAILABLE', label: 'Dostępna', color: 'green' },
                    { value: 'BLOCKED', label: 'Zablokowana', color: 'red' },
                    { value: 'EXPIRED', label: 'Przeterminowana', color: 'gray' },
                ];
                const currentOption = statusOptions.find(o => o.value === status);
                return (
                    <Select
                        size="small"
                        value={status}
                        style={{ width: 120 }}
                        options={statusOptions.map(o => ({
                            value: o.value,
                            label: <Tag color={o.color}>{o.label}</Tag>
                        }))}
                        onChange={(newStatus) => {
                            updateBatchMutation.mutate({ id: record.id, data: { status: newStatus } });
                        }}
                    />
                );
            },
        },
        {
            title: 'Data ważności',
            dataIndex: 'expiryDate',
            key: 'expiryDate',
            sorter: (a: any, b: any) => dayjs(a.expiryDate).valueOf() - dayjs(b.expiryDate).valueOf(),
            defaultSortOrder: 'ascend' as const,
            render: (date: string) => {
                const daysLeft = dayjs(date).diff(dayjs(), 'day');
                let color = 'green';
                if (daysLeft <= 14) color = 'red';
                else if (daysLeft <= 30) color = 'orange';
                return (
                    <Space>
                        {dayjs(date).format('DD.MM.YYYY')}
                        {daysLeft <= 30 && (
                            <Badge
                                count={`${daysLeft}d`}
                                style={{ backgroundColor: color === 'red' ? '#ff4d4f' : '#faad14' }}
                            />
                        )}
                    </Space>
                );
            },
        },
        {
            title: 'Ilość',
            key: 'quantity',
            render: (_: any, record: any) => {
                const current = record.currentStock ?? 0;
                const initial = record.initialQuantity ?? 0;
                return (
                    <span>
                        <strong>{current}</strong>
                        <span style={{ color: '#888', fontSize: '11px', marginLeft: '4px' }}>
                            ({initial} pocz.)
                        </span>
                    </span>
                );
            },
        },
        {
            title: 'Landed Cost',
            dataIndex: 'landedCost',
            key: 'landedCost',
            render: (cost: number) =>
                cost
                    ? new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN' }).format(cost)
                    : '-',
        },
        {
            title: 'Akcje',
            key: 'actions',
            width: 160,
            render: (_: any, record: any) => (
                <Space>
                    <Button
                        type="text"
                        size="small"
                        onClick={() => handleEditBatch(record)}
                    >
                        Edytuj
                    </Button>
                    <Tooltip title="Transfer">
                        <Button
                            type="text"
                            size="small"
                            icon={<SwapOutlined />}
                            onClick={() => openTransferModal(record)}
                        />
                    </Tooltip>
                    <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => confirmDeleteBatch(record.id, record.lotNumber)}
                    />
                </Space>
            ),
        },
    ];

    const eventColumns = [
        {
            title: 'Data',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (date: string) => dayjs(date).format('DD.MM.YYYY HH:mm'),
            width: 140,
        },
        {
            title: 'Produkt',
            key: 'product',
            render: (_: any, record: any) => record.product?.name || record.productId,
        },
        {
            title: 'Typ',
            dataIndex: 'eventType',
            key: 'eventType',
            render: (type: string) => {
                const colors: Record<string, string> = {
                    SALE: 'red',
                    PURCHASE_RECEIPT: 'green',
                    ADJUSTMENT_PLUS: 'blue',
                    ADJUSTMENT_MINUS: 'orange',
                };
                return <Tag color={colors[type] || 'default'}>{type}</Tag>;
            },
        },
        {
            title: 'Ilość',
            dataIndex: 'quantity',
            key: 'quantity',
            render: (qty: number) => (
                <span style={{ color: qty > 0 ? '#52c41a' : '#ff4d4f' }}>
                    {qty > 0 ? '+' : ''}
                    {qty}
                </span>
            ),
        },
        {
            title: 'Lokalizacja',
            key: 'location',
            render: (_: any, record: any) => record.location?.name || record.locationId || '-',
        },
        { title: 'Notatki', dataIndex: 'notes', key: 'notes' },
    ];


    const locationColumns = [
        { title: 'Kod', dataIndex: 'code', key: 'code', width: 100 },
        { title: 'Nazwa', dataIndex: 'name', key: 'name' },
        {
            title: 'Typ',
            dataIndex: 'type',
            key: 'type',
            width: 140,
            render: (type: string) => {
                const typeLabels: Record<string, string> = {
                    WAREHOUSE: 'Magazyn',
                    DAMAGED: 'Uszkodzone',
                    DISPOSAL: 'Utylizacja',
                    RECALLED: 'Wycofane',
                    MARKETING: 'Marketing',
                };
                return typeLabels[type] || type;
            },
        },
        { title: 'Opis', dataIndex: 'description', key: 'description' },
        {
            title: 'Akcje',
            key: 'actions',
            width: 120,
            render: (_: any, record: any) => (
                <Space>
                    <Button
                        size="small"
                        onClick={() => {
                            setEditingLocation(record);
                            locationForm.setFieldsValue(record);
                            setIsLocationModalOpen(true);
                        }}
                    >
                        Edytuj
                    </Button>
                    <Button
                        size="small"
                        danger
                        onClick={() => confirmDeleteLocation(record.id, record.name)}
                    >
                        Usuń
                    </Button>
                </Space>
            ),
        },
    ];

    // Options for dropdowns - must be defined before tabItems
    const productOptions = (productsData?.data || []).map((p: any) => ({
        label: `${p.sku} - ${p.name}`,
        value: p.id,
    }));

    const locationOptions = (locationsData || []).map((l: any) => ({
        label: `${l.code} - ${l.name}`,
        value: l.id,
    }));

    const activeBatches = batchesData?.filter((b: any) => b.status === 'AVAILABLE').length || 0;
    const expiringBatches = batchesData?.filter((b: any) => {
        const daysLeft = dayjs(b.expiryDate).diff(dayjs(), 'day');
        return daysLeft <= 30 && b.status === 'AVAILABLE';
    }).length || 0;

    const stockActionButtons = (
        <Space wrap={isMobile} style={{ width: isMobile ? '100%' : 'auto' }}>
            <Dropdown
                menu={{
                    items: [
                        {
                            key: 'csv',
                            label: 'Eksport CSV',
                            onClick: async () => {
                                try {
                                    const authData = JSON.parse(localStorage.getItem('curecraft-auth') || '{}');
                                    const token = authData?.state?.token;
                                    if (!token) {
                                        message.error('Brak autoryzacji');
                                        return;
                                    }
                                    const response = await fetch('/api/inventory/export', {
                                        headers: { Authorization: `Bearer ${token}` },
                                    });
                                    const blob = await response.blob();
                                    const url = window.URL.createObjectURL(blob);
                                    const a = document.createElement('a');
                                    a.href = url;
                                    a.download = `stany-magazynowe_${new Date().toISOString().slice(0, 10)}.csv`;
                                    a.click();
                                    window.URL.revokeObjectURL(url);
                                } catch (e) {
                                    message.error('Błąd eksportu');
                                }
                            },
                        },
                        {
                            key: 'xlsx',
                            label: 'Eksport XLSX (Excel)',
                            onClick: async () => {
                                try {
                                    const { exportToXLSX } = await import('../../utils/exportUtils');
                                    await exportToXLSX(
                                        stockData || [],
                                        'stany-magazynowe',
                                        [
                                            { key: 'productName', title: 'Produkt' },
                                            { key: 'locationName', title: 'Lokalizacja' },
                                            { key: 'available', title: 'Dostępne' },
                                            { key: 'reserved', title: 'Zarezerwowane' },
                                            { key: 'blocked', title: 'Zablokowane' },
                                            { key: 'total', title: 'Razem' },
                                        ]
                                    );
                                } catch (e) {
                                    message.error('Błąd eksportu XLSX');
                                }
                            },
                        },
                    ],
                }}
            >
                <Button icon={<DownloadOutlined />} style={{ flex: isMobile ? 1 : 'none' }}>
                    Eksportuj {!isMobile && '▾'}
                </Button>
            </Dropdown>
            <Button
                icon={<SyncOutlined />}
                style={{ flex: isMobile ? 1 : 'none' }}
                onClick={async () => {
                    const hide = message.loading('Synchronizacja z Allegro...', 0);
                    try {
                        const result = await integrationsApi.syncFulfillmentStock();
                        hide();
                        const data = result.data;
                        if (data.status === 'success') {
                            message.success(data.message);
                            queryClient.invalidateQueries({ queryKey: ['stock'] });
                        } else {
                            message.error(data.message || 'Błąd synchronizacji');
                        }
                    } catch (e: any) {
                        hide();
                        message.error(e.response?.data?.message || 'Błąd synchronizacji z Allegro');
                    }
                }}
            >
                Sync {!isMobile && 'z Allegro'}
            </Button>
        </Space>
    );

    const tabItems = [
        {
            key: 'stock',
            label: 'Stany',
            children: (
                <Space direction="vertical" style={{ width: '100%' }}>
                    <Row gutter={[8, 8]} style={{ marginBottom: 16 }}>
                        <Col xs={24} sm={10}>
                            <Select
                                placeholder="Szukaj produktu..."
                                options={productOptions}
                                value={filterProduct}
                                onChange={setFilterProduct}
                                allowClear
                                showSearch
                                optionFilterProp="label"
                                style={{ width: '100%' }}
                            />
                        </Col>
                        <Col xs={24} sm={10}>
                            <Select
                                placeholder="Lokalizacja"
                                options={locationOptions}
                                value={filterLocation}
                                onChange={setFilterLocation}
                                allowClear
                                showSearch
                                optionFilterProp="label"
                                style={{ width: '100%' }}
                            />
                        </Col>
                        {isMobile && <Col xs={24}>{stockActionButtons}</Col>}
                        {!isMobile && <Col span={4}>{stockActionButtons}</Col>}
                    </Row>
                    {isMobile ? (
                        <List
                            dataSource={aggregatedStock}
                            loading={stockLoading}
                            renderItem={(record: any) => (
                                <List.Item style={{ padding: '8px 0' }}>
                                    <Card size="small" style={{ width: '100%', borderRadius: 8 }}>
                                        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8 }}>{record.productName}</div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                            <span style={{ color: '#999' }}>Dostępne:</span>
                                            <Tag color={(record.totalAvailable || 0) > 0 ? 'green' : 'red'} style={{ margin: 0 }}>
                                                {record.totalAvailable || 0} szt.
                                            </Tag>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <span style={{ color: '#999' }}>Łącznie:</span>
                                            <span style={{ fontWeight: 600 }}>{record.total || 0} szt.</span>
                                        </div>
                                        {record.children?.length > 0 && (
                                            <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #f0f0f0' }}>
                                                {record.children.map((child: any) => (
                                                    <div key={child.key} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                                                        <span style={{ color: '#666' }}>{child.productName}:</span>
                                                        <span>{child.totalAvailable} szt.</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </Card>
                                </List.Item>
                            )}
                        />
                    ) : (
                        <Table
                            dataSource={aggregatedStock}
                            columns={stockColumns}
                            rowKey="key"
                            loading={stockLoading}
                            scroll={{ x: 800 }}
                            pagination={false}
                            size="small"
                            indentSize={32}
                            rowClassName={(record: any) => record.isChild ? 'child-stock-row' : 'parent-stock-row'}
                        />
                    )}
                </Space>
            ),
        },
        {
            key: 'batches',
            label: 'Partie (LOT)',
            children: (
                <Space direction="vertical" style={{ width: '100%' }}>
                    <Row gutter={[8, 8]} style={{ marginBottom: 16 }}>
                        <Col xs={24} sm={10}>
                            <Select
                                placeholder="Szukaj produktu..."
                                options={productOptions}
                                value={filterProduct}
                                onChange={setFilterProduct}
                                allowClear
                                showSearch
                                optionFilterProp="label"
                                style={{ width: '100%' }}
                            />
                        </Col>
                        <Col xs={24} sm={10}>
                            <Select
                                placeholder="Status"
                                options={[
                                    { label: 'Dostępne', value: 'AVAILABLE' },
                                    { label: 'Zarezerwowane', value: 'RESERVED' },
                                    { label: 'Wygasłe', value: 'EXPIRED' },
                                    { label: 'Zużyte', value: 'DEPLETED' },
                                ]}
                                value={filterBatchStatus}
                                onChange={setFilterBatchStatus}
                                allowClear
                                style={{ width: '100%' }}
                            />
                        </Col>
                        <Col xs={24} sm={4}>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                block
                                onClick={() => setIsBatchModalOpen(true)}
                            >
                                Nowa partia
                            </Button>
                        </Col>
                    </Row>
                    {isMobile ? (
                        <List
                            dataSource={batchesData || []}
                            loading={batchesLoading}
                            renderItem={(record: any) => (
                                <List.Item style={{ padding: '8px 0' }}>
                                    <Card
                                        size="small"
                                        style={{ width: '100%', borderRadius: 8 }}
                                        onClick={() => handleEditBatch(record)}
                                        hoverable
                                    >
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                                            <div style={{ fontWeight: 600, fontSize: 14, color: '#1890ff' }}>{record.lotNumber}</div>
                                            <Tag color={
                                                record.status === 'AVAILABLE' ? 'green' :
                                                record.status === 'EXPIRED' ? 'red' :
                                                'blue'
                                            } style={{ margin: 0, fontSize: 10 }}>{record.status}</Tag>
                                        </div>
                                        <div style={{ fontSize: 13, marginBottom: 8, fontWeight: 500 }}>{record.product?.name}</div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#666', marginBottom: 8 }}>
                                            <span>Ważność: {dayjs(record.expiryDate).format('DD.MM.YYYY')}</span>
                                            <span>Ilość: <strong>{record.currentStock} szt.</strong></span>
                                        </div>
                                        <div style={{ display: 'flex', gap: 8 }}>
                                            <Button size="small" icon={<SwapOutlined />} onClick={(e) => { e.stopPropagation(); openTransferModal(record); }}>Transfer</Button>
                                            <Button size="small" danger icon={<DeleteOutlined />} onClick={(e) => { e.stopPropagation(); confirmDeleteBatch(record.id, record.lotNumber); }} />
                                        </div>
                                    </Card>
                                </List.Item>
                            )}
                        />
                    ) : (
                        <Table
                            dataSource={batchesData || []}
                            columns={batchColumns}
                            rowKey="id"
                            loading={batchesLoading}
                            scroll={{ x: 1000 }}
                            pagination={{ pageSize: 20 }}
                        />
                    )}
                </Space>
            ),
        },
        {
            key: 'locations',
            label: 'Lokalizacje',
            children: (
                <Space direction="vertical" style={{ width: '100%' }}>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => setIsLocationModalOpen(true)}
                        >
                            Dodaj lokalizację
                        </Button>
                    </div>
                    {isMobile ? (
                        <List
                            dataSource={locationsData || []}
                            renderItem={(record: any) => (
                                <List.Item style={{ padding: '8px 0' }}>
                                    <Card size="small" style={{ width: '100%', borderRadius: 8 }}>
                                        <div style={{ fontWeight: 600, fontSize: 14 }}>{record.name}</div>
                                        <div style={{ fontSize: 12, color: '#999', marginBottom: 8 }}>Kod: {record.code}</div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Tag>{record.type}</Tag>
                                            <Space>
                                                <Button size="small" onClick={() => {
                                                    setEditingLocation(record);
                                                    locationForm.setFieldsValue(record);
                                                    setIsLocationModalOpen(true);
                                                }}>Edytuj</Button>
                                                <Button size="small" danger onClick={() => confirmDeleteLocation(record.id, record.name)}>Usuń</Button>
                                            </Space>
                                        </div>
                                    </Card>
                                </List.Item>
                            )}
                        />
                    ) : (
                        <Table dataSource={locationsData || []} columns={locationColumns} rowKey="id" scroll={{ x: 600 }} />
                    )}
                </Space>
            ),
        },
        {
            key: 'events',
            label: 'Zdarzenia',
            children: (
                <Space direction="vertical" style={{ width: '100%' }}>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => setIsEventModalOpen(true)}
                        >
                            Dodaj zdarzenie
                        </Button>
                    </div>
                    {isMobile ? (
                        <List
                            dataSource={eventsData || []}
                            loading={eventsLoading}
                            pagination={{ pageSize: 10, size: 'small' }}
                            renderItem={(record: any) => (
                                <List.Item style={{ padding: '8px 0' }}>
                                    <Card size="small" style={{ width: '100%', borderRadius: 8 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                            <span style={{ fontSize: 12, color: '#999' }}>{dayjs(record.createdAt).format('DD.MM.YYYY HH:mm')}</span>
                                            <Tag color={
                                                record.eventType === 'SALE' ? 'red' :
                                                record.eventType === 'PURCHASE_RECEIPT' ? 'green' :
                                                'blue'
                                            } style={{ margin: 0, fontSize: 10 }}>{record.eventType}</Tag>
                                        </div>
                                        <div style={{ fontWeight: 500, marginBottom: 4 }}>{record.product?.name}</div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <span style={{ fontSize: 12 }}>{record.location?.name || '-'}</span>
                                            <span style={{ fontWeight: 700, color: record.quantity > 0 ? '#52c41a' : '#ff4d4f' }}>
                                                {record.quantity > 0 ? '+' : ''}{record.quantity}
                                            </span>
                                        </div>
                                    </Card>
                                </List.Item>
                            )}
                        />
                    ) : (
                        <Table
                            dataSource={eventsData || []}
                            columns={eventColumns}
                            rowKey="id"
                            loading={eventsLoading}
                            pagination={{ pageSize: 20 }}
                            scroll={{ x: 800 }}
                        />
                    )}
                </Space>
            ),
        },

        {
            key: 'alerts',
            label: (
                <Badge count={(alertsData || []).filter((a: any) => a.status === 'ACTIVE').length} offset={[10, 0]}>
                    <span>Alerty</span>
                </Badge>
            ),
            children: (
                <Space direction="vertical" style={{ width: '100%' }}>
                    <Row justify="end" style={{ marginBottom: 16 }}>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={() => generateAlertsMutation.mutate()}
                            loading={generateAlertsMutation.isPending}
                        >
                            Skanuj
                        </Button>
                    </Row>
                    {alertsLoading ? (
                        <Card>
                            <p style={{ textAlign: 'center' }}>Ładowanie alertów...</p>
                        </Card>
                    ) : (!alertsData || alertsData.length === 0) ? (
                        <Card>
                            <p style={{ textAlign: 'center', color: '#52c41a' }}>
                                ✅ Brak alertów - wszystkie stany magazynowe w normie
                            </p>
                        </Card>
                    ) : isMobile ? (
                        <List
                            dataSource={alertsData || []}
                            renderItem={(record: any) => (
                                <List.Item style={{ padding: '8px 0' }}>
                                    <Card
                                        size="small"
                                        style={{ 
                                            width: '100%', 
                                            borderRadius: 8,
                                            borderLeft: `4px solid ${
                                                record.severity === 'CRITICAL' ? '#ff4d4f' : 
                                                record.severity === 'HIGH' ? '#faad14' : 
                                                '#1890ff'
                                            }` 
                                        }}
                                    >
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                            <Tag color={
                                                record.severity === 'CRITICAL' ? 'red' : 
                                                record.severity === 'HIGH' ? 'orange' : 
                                                'blue'
                                            } style={{ fontSize: 10 }}>{record.severity}</Tag>
                                            <span style={{ fontSize: 11, color: '#999' }}>{dayjs(record.createdAt).format('DD.MM HH:mm')}</span>
                                        </div>
                                        <div style={{ fontWeight: 600, marginBottom: 4 }}>{record.message}</div>
                                        <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
                                            {record.product?.name} {record.batch?.lotNumber && `(LOT: ${record.batch.lotNumber})`}
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Tag color={
                                                record.status === 'ACTIVE' ? 'red' :
                                                record.status === 'ACKNOWLEDGED' ? 'orange' :
                                                'green'
                                            } style={{ margin: 0 }}>{record.status}</Tag>
                                            <Space>
                                                {record.status === 'ACTIVE' && (
                                                    <Button size="small" icon={<CheckOutlined />} onClick={() => acknowledgeAlertMutation.mutate({ id: record.id })} />
                                                )}
                                                <Button size="small" style={{ color: '#52c41a' }} onClick={() => resolveAlertMutation.mutate(record.id)}>✓</Button>
                                            </Space>
                                        </div>
                                    </Card>
                                </List.Item>
                            )}
                        />
                    ) : (
                        <Table
                            dataSource={alertsData || []}
                            columns={[
                                {
                                    title: 'Ważność',
                                    dataIndex: 'severity',
                                    key: 'severity',
                                    width: 100,
                                    render: (sev: string) => {
                                        const colors: Record<string, string> = {
                                            CRITICAL: 'red',
                                            HIGH: 'orange',
                                            MEDIUM: 'gold',
                                            LOW: 'blue',
                                        };
                                        const labels: Record<string, string> = {
                                            CRITICAL: 'KRYTYCZNY',
                                            HIGH: 'WYSOKI',
                                            MEDIUM: 'ŚREDNI',
                                            LOW: 'NISKI',
                                        };
                                        return <Tag color={colors[sev] || 'default'}>{labels[sev] || sev}</Tag>;
                                    }
                                },
                                {
                                    title: 'Typ',
                                    dataIndex: 'alertType',
                                    key: 'alertType',
                                    width: 140,
                                    render: (type: string) => {
                                        const labels: Record<string, string> = {
                                            LOW_STOCK: 'Niski stan',
                                            OUT_OF_STOCK: 'Brak towaru',
                                            EXPIRED: 'Przeterminowane',
                                            NEAR_EXPIRY: 'Blisko wygaśnięcia',
                                            BLOCKED_BATCH: 'Zablokowana partia',
                                        };
                                        return labels[type] || type;
                                    }
                                },
                                {
                                    title: 'Wiadomość',
                                    dataIndex: 'message',
                                    key: 'message',
                                },
                                {
                                    title: 'Produkt',
                                    key: 'product',
                                    width: 150,
                                    render: (_: any, record: any) => record.product?.name || '-',
                                },
                                {
                                    title: 'Partia',
                                    key: 'batch',
                                    width: 120,
                                    render: (_: any, record: any) => record.batch?.lotNumber || '-',
                                },
                                {
                                    title: 'Status',
                                    dataIndex: 'status',
                                    key: 'status',
                                    width: 120,
                                    render: (status: string) => {
                                        const colors: Record<string, string> = {
                                            ACTIVE: 'red',
                                            ACKNOWLEDGED: 'orange',
                                            RESOLVED: 'green',
                                            DISMISSED: 'gray',
                                        };
                                        const labels: Record<string, string> = {
                                            ACTIVE: 'Aktywny',
                                            ACKNOWLEDGED: 'Potwierdzony',
                                            RESOLVED: 'Rozwiązany',
                                            DISMISSED: 'Odrzucony',
                                        };
                                        return <Tag color={colors[status] || 'default'}>{labels[status] || status}</Tag>;
                                    }
                                },
                                {
                                    title: 'Data',
                                    dataIndex: 'createdAt',
                                    key: 'createdAt',
                                    width: 140,
                                    render: (date: string) => dayjs(date).format('DD.MM.YYYY HH:mm'),
                                },
                                {
                                    title: 'Akcje',
                                    key: 'actions',
                                    width: 180,
                                    render: (_: any, record: any) => (
                                        <Space>
                                            {record.status === 'ACTIVE' && (
                                                <Tooltip title="Potwierdź">
                                                    <Button
                                                        type="text"
                                                        size="small"
                                                        icon={<CheckOutlined />}
                                                        onClick={() => acknowledgeAlertMutation.mutate({ id: record.id })}
                                                        loading={acknowledgeAlertMutation.isPending}
                                                    />
                                                </Tooltip>
                                            )}
                                            {(record.status === 'ACTIVE' || record.status === 'ACKNOWLEDGED') && (
                                                <>
                                                    <Tooltip title="Rozwiąż">
                                                        <Button
                                                            type="text"
                                                            size="small"
                                                            style={{ color: '#52c41a' }}
                                                            onClick={() => resolveAlertMutation.mutate(record.id)}
                                                            loading={resolveAlertMutation.isPending}
                                                        >
                                                            ✓ OK
                                                        </Button>
                                                    </Tooltip>
                                                    <Tooltip title="Odrzuć">
                                                        <Button
                                                            type="text"
                                                            size="small"
                                                            danger
                                                            icon={<DeleteOutlined />}
                                                            onClick={() => dismissAlertMutation.mutate(record.id)}
                                                            loading={dismissAlertMutation.isPending}
                                                        />
                                                    </Tooltip>
                                                </>
                                            )}
                                        </Space>
                                    ),
                                },
                            ]}
                            rowKey="id"
                            loading={alertsLoading}
                            pagination={{ pageSize: 20 }}
                            scroll={{ x: 1200 }}
                        />
                    )}
                </Space>
            ),
        },

    ];

    return (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Row gutter={[16, 16]}>
                <Col xs={24} sm={8}>
                    <Card size="small">
                        <Statistic
                            title={
                                <Space>
                                    Lokalizacje
                                    <Tooltip title="Liczba zdefiniowanych lokalizacji magazynowych">
                                        <InfoCircleOutlined style={{ color: '#999' }} />
                                    </Tooltip>
                                </Space>
                            }
                            value={locationsData?.length || 0}
                            prefix={<EnvironmentOutlined />}
                            valueStyle={{ fontSize: isMobile ? 20 : 24 }}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={8}>
                    <Card size="small">
                        <Statistic
                            title={
                                <Space>
                                    Partie aktywne
                                    <Tooltip title="Partie ze statusem AVAILABLE gotowe do sprzedaży">
                                        <InfoCircleOutlined style={{ color: '#999' }} />
                                    </Tooltip>
                                </Space>
                            }
                            value={activeBatches}
                            prefix={<InboxOutlined />}
                            valueStyle={{ fontSize: isMobile ? 20 : 24 }}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={8}>
                    <Card size="small">
                        <Statistic
                            title={
                                <Space>
                                    Wygasające
                                    <Tooltip title="Partie z datą ważności w ciągu 30 dni">
                                        <InfoCircleOutlined style={{ color: '#999' }} />
                                    </Tooltip>
                                </Space>
                            }
                            value={expiringBatches}
                            valueStyle={{ color: expiringBatches > 0 ? '#ff4d4f' : undefined, fontSize: isMobile ? 20 : 24 }}
                        />
                    </Card>
                </Col>
            </Row>

            <Card styles={{ body: { padding: isMobile ? '8px' : '24px' } }}>
                <Tabs items={tabItems} size={isMobile ? 'small' : 'middle'} />
            </Card>

            {/* Location Modal */}
            <Modal
                title={editingLocation ? 'Edytuj lokalizację' : 'Nowa lokalizacja'}
                open={isLocationModalOpen}
                onCancel={() => {
                    setIsLocationModalOpen(false);
                    setEditingLocation(null);
                    locationForm.resetFields();
                }}
                onOk={() => locationForm.submit()}
                confirmLoading={createLocationMutation.isPending || updateLocationMutation.isPending}
                width={isMobile ? '100%' : 520}
                style={isMobile ? { top: 0, margin: 0, maxWidth: '100vw' } : {}}
            >
                <Form
                    form={locationForm}
                    layout="vertical"
                    onFinish={(values) => {
                        if (editingLocation) {
                            updateLocationMutation.mutate({ id: editingLocation.id, data: values });
                        } else {
                            createLocationMutation.mutate(values);
                        }
                    }}
                >
                    <Form.Item
                        name="code"
                        label="Kod"
                        rules={[{ required: true, message: 'Podaj kod' }]}
                    >
                        <Input placeholder="np. MAG-A1" />
                    </Form.Item>
                    <Form.Item
                        name="name"
                        label="Nazwa"
                        rules={[{ required: true, message: 'Podaj nazwę' }]}
                    >
                        <Input placeholder="np. Magazyn główny - regał A1" />
                    </Form.Item>
                    <Form.Item name="type" label="Typ">
                        <Select
                            options={[
                                { label: 'Magazyn', value: 'WAREHOUSE' },
                                { label: 'Uszkodzone', value: 'DAMAGED' },
                                { label: 'Utylizacja', value: 'DISPOSAL' },
                                { label: 'Wycofane', value: 'RECALLED' },
                                { label: 'Marketing', value: 'MARKETING' },
                            ]}
                        />
                    </Form.Item>
                    <Form.Item name="description" label="Opis">
                        <Input.TextArea rows={2} />
                    </Form.Item>
                </Form>
            </Modal>

            {/* Batch Modal */}
            <Modal
                title={editingBatch ? 'Edytuj partię' : 'Nowa partia (LOT)'}
                open={isBatchModalOpen}
                onCancel={() => {
                    setIsBatchModalOpen(false);
                    setEditingBatch(null);
                    batchForm.resetFields();
                }}
                onOk={() => batchForm.submit()}
                confirmLoading={createBatchMutation.isPending || updateBatchMutation.isPending}
                width={isMobile ? '100%' : 600}
                style={isMobile ? { top: 0, margin: 0, maxWidth: '100vw' } : {}}
            >
                <Form
                    form={batchForm}
                    layout="vertical"
                    onFinish={(values: any) => {
                        const data = {
                            lotNumber: values.lotNumber,
                            productId: values.productId,
                            locationId: values.locationId,
                            expiryDate: values.expiryDate?.toISOString(),
                            productionDate: values.productionDate?.toISOString() || new Date().toISOString(),
                            initialQuantity: values.quantity,
                            unitCost: values.unitCost || 0,
                            landedCost: values.landedCost || values.unitCost || 0,
                            status: values.status || 'AVAILABLE',
                        };
                        if (editingBatch) {
                            updateBatchMutation.mutate({ id: editingBatch.id, data });
                        } else {
                            createBatchMutation.mutate(data);
                        }
                    }}
                >
                    <Form.Item
                        name="lotNumber"
                        label="Numer LOT"
                        rules={[{ required: true, message: 'Podaj numer LOT' }]}
                    >
                        <Input placeholder="np. LOT-2026-01-001" />
                    </Form.Item>
                    <Form.Item
                        name="productId"
                        label="Produkt"
                        rules={[{ required: true, message: 'Wybierz produkt' }]}
                    >
                        <Select options={productOptions} showSearch optionFilterProp="label" />
                    </Form.Item>
                    <Form.Item name="locationId" label="Lokalizacja">
                        <Select options={locationOptions} showSearch optionFilterProp="label" allowClear />
                    </Form.Item>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="productionDate" label="Data produkcji">
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="expiryDate"
                                label="Data ważności"
                                rules={[{ required: true, message: 'Podaj datę ważności' }]}
                            >
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="quantity" label="Ilość" rules={[{ required: true }]}>
                                <InputNumber style={{ width: '100%' }} min={1} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="unitCost" label="Koszt jednostkowy">
                                <InputNumber style={{ width: '100%' }} min={0} precision={2} addonAfter="PLN" />
                            </Form.Item>
                        </Col>
                    </Row>

                    {editingBatch && (
                        <BatchPackagingSection batchId={editingBatch.id} />
                    )}
                </Form>
            </Modal>

            {/* Event Modal */}
            <Modal
                title="Nowe zdarzenie magazynowe"
                open={isEventModalOpen}
                onCancel={() => {
                    setIsEventModalOpen(false);
                    eventForm.resetFields();
                }}
                onOk={() => eventForm.submit()}
                confirmLoading={createEventMutation.isPending}
                width={isMobile ? '100%' : 520}
                style={isMobile ? { top: 0, margin: 0, maxWidth: '100vw' } : {}}
            >
                <Form
                    form={eventForm}
                    layout="vertical"
                    onFinish={(values) => {
                        // Negate quantity for events that reduce inventory
                        let quantity = Number(values.quantity);
                        if (values.eventType === 'SALE' || values.eventType === 'ADJUSTMENT_MINUS') {
                            quantity = -Math.abs(quantity);
                        } else {
                            quantity = Math.abs(quantity);
                        }
                        createEventMutation.mutate({
                            ...values,
                            quantity,
                        });
                    }}
                >

                    <Form.Item
                        name="eventType"
                        label="Typ zdarzenia"
                        rules={[{ required: true }]}
                    >
                        <Select
                            options={[
                                { label: 'Przyjęcie towaru', value: 'PURCHASE_RECEIPT' },
                                { label: 'Sprzedaż', value: 'SALE' },
                                { label: 'Korekta +', value: 'ADJUSTMENT_PLUS' },
                                { label: 'Korekta -', value: 'ADJUSTMENT_MINUS' },
                            ]}
                        />
                    </Form.Item>
                    <Form.Item name="productId" label="Produkt" rules={[{ required: true }]}>
                        <Select
                            options={productOptions}
                            showSearch
                            optionFilterProp="label"
                            onChange={() => eventForm.setFieldValue('batchId', undefined)}
                        />
                    </Form.Item>
                    <Form.Item
                        noStyle
                        shouldUpdate={(prevValues, currentValues) => prevValues.productId !== currentValues.productId}
                    >
                        {({ getFieldValue }) => {
                            const productId = getFieldValue('productId');
                            const filteredBatches = batchesData?.filter((b: any) => b.productId === productId) || [];
                            const filteredBatchOptions = filteredBatches.map((b: any) => ({
                                label: `${b.lotNumber} (ważność: ${dayjs(b.expiryDate).format('YYYY-MM-DD')})`,
                                value: b.id,
                            }));

                            return (
                                <Form.Item name="batchId" label="Partia (opcjonalnie)">
                                    <Select
                                        options={filteredBatchOptions}
                                        placeholder="Wybierz partie"
                                        allowClear
                                        disabled={!productId}
                                    />
                                </Form.Item>
                            );
                        }}
                    </Form.Item>
                    <Form.Item name="locationId" label="Lokalizacja (opcjonalnie)">
                        <Select
                            options={locationsData?.map((l: any) => ({ label: l.name, value: l.id }))}
                            placeholder="Wybierz lokalizację"
                            allowClear
                        />
                    </Form.Item>
                    <Form.Item name="quantity" label="Ilość" rules={[{ required: true }]}>
                        <InputNumber style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="notes" label="Notatki">
                        <Input.TextArea rows={2} />
                    </Form.Item>
                </Form>
            </Modal>

            {/* Transfer Batch Modal */}
            <Modal
                title="Transfer partii między lokalizacjami"
                open={isTransferModalOpen}
                onCancel={() => {
                    setIsTransferModalOpen(false);
                    setTransferringBatch(null);
                    transferForm.resetFields();
                }}
                onOk={() => transferForm.submit()}
                confirmLoading={transferBatchMutation.isPending}
                width={isMobile ? '100%' : 520}
                style={isMobile ? { top: 0, margin: 0, maxWidth: '100vw' } : {}}
            >
                {transferringBatch && (
                    <div style={{ marginBottom: 16, background: '#f5f5f5', padding: 12, borderRadius: 8 }}>
                        <strong>Partia:</strong> {transferringBatch.lotNumber}<br />
                        <strong>Produkt:</strong> {transferringBatch.product?.name || transferringBatch.productId}<br />
                        <strong>Aktualna lokalizacja:</strong> {transferringBatch.location?.name || 'Brak'}
                    </div>
                )}
                <Form
                    form={transferForm}
                    layout="vertical"
                    onFinish={(values: any) => {
                        if (!transferringBatch) return;
                        transferBatchMutation.mutate({
                            batchId: transferringBatch.id,
                            fromLocationId: values.fromLocationId,
                            toLocationId: values.toLocationId,
                            quantity: values.quantity,
                            notes: values.notes,
                        });
                    }}
                >
                    <Form.Item name="fromLocationId" label="Z lokalizacji" rules={[{ required: true, message: 'Wybierz źródłową lokalizację' }]}>
                        <Select
                            options={locationsData?.map((l: any) => ({ label: l.name, value: l.id }))}
                            placeholder="Wybierz lokalizację źródłową"
                        />
                    </Form.Item>
                    <Form.Item name="toLocationId" label="Do lokalizacji" rules={[{ required: true, message: 'Wybierz docelową lokalizację' }]}>
                        <Select
                            options={locationsData?.map((l: any) => ({ label: l.name, value: l.id }))}
                            placeholder="Wybierz lokalizację docelową"
                        />
                    </Form.Item>
                    <Form.Item name="quantity" label="Ilość do transferu" rules={[{ required: true, message: 'Podaj ilość' }]}>
                        <InputNumber min={1} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="notes" label="Notatki (opcjonalne)">
                        <Input.TextArea rows={2} placeholder="np. Transfer na Allegro FBA" />
                    </Form.Item>
                </Form>
            </Modal>

            <style>{`
                .parent-stock-row { 
                    background-color: #f5f5f5 !important;
                }
                .parent-stock-row td {
                    border-bottom: 2px solid #e8e8e8 !important;
                }
                .child-stock-row {
                    background-color: #ffffff !important;
                }
                .child-stock-row .ant-table-row-indent {
                    padding-left: 12px !important;
                }
                /* Dark theme overrides */
                .dark-theme .parent-stock-row {
                    background-color: #1f1f1f !important;
                }
                .dark-theme .parent-stock-row td {
                    border-bottom: 2px solid #303030 !important;
                }
                .dark-theme .child-stock-row {
                    background-color: #141414 !important;
                }
                .dark-theme .child-stock-row td {
                    color: rgba(255, 255, 255, 0.85);
                }
            `}</style>
        </Space>
    );
}
